import React, { useState } from 'react';
import styled from 'styled-components';
import { lighten } from 'polished';
//
import colors from 'theme/colors';
import { truncate } from 'lib/helpers/string-manipulators';

const Container = styled.div`
  height: 38px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const colorGradient = 0.2;
const colorGradientHigh = 0.3;

const Button = styled.button<{ active: boolean; color: string }>`
  background-color: ${props => (props.active ? lighten(colorGradientHigh, props.color) : 'transparent')};
  border-color: ${props => (props.active ? props.color : colors.globalInputDisabled)};
  border-radius: 16px;
  border-width: 1px;
  border-style: solid;
  color: ${props => (props.active ? props.color : colors.fontBody)};
  min-height: 38px;
  min-width: 120px;
  transition: color 0.2s ease, border-color 0.2s ease;

  :active,
  :focus {
    outline: none;
  }

  :hover {
    cursor: pointer;
    color: ${props => lighten(colorGradient, props.color)};
    border-color: ${props => lighten(colorGradient, props.color)};
  }
`;

const Text = styled.span`
  font-weight: 500;
  line-height: 1em;
  padding: 8px;
`;

const Subtext = styled.p`
  font-size: 12px;
  margin-top: 8px;
  margin-left: 16px;
  width: 0px; // We do not want the button spacing to change just because the subtext is long.
  white-space: nowrap;
  color: ${colors.globalIcon};
`;

export interface IBubblyButtonProps {
  color?: string;
  text: string;
  subtext?: string;
  active: boolean;
  onClick: () => void;
  onHover?: () => void;
  onBlur?: () => void;
}

export default ({ color = colors.primary, text, subtext, active, onClick, onHover, onBlur }: IBubblyButtonProps) => {
  const [showSubtext, setShowSubtext] = useState(false);

  const setFocus = () => {
    setShowSubtext(true);
    if (onHover) {
      onHover();
    }
  };

  const unsetFocus = () => {
    setShowSubtext(false);
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <Container>
      <Button
        color={color}
        active={active}
        onClick={onClick}
        onFocus={setFocus}
        onBlur={unsetFocus}
        onMouseOver={setFocus}
        onMouseOut={unsetFocus}
        data-testid="bubbly-text-button"
      >
        <Text>{text}</Text>
      </Button>
      {subtext && showSubtext ? <Subtext>{truncate(subtext, { maxLength: 60 })}</Subtext> : null}
    </Container>
  );
};
